import { FC, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router';
import i18next from 'i18next';
import { CLIENT_LOGIN_LINK, CLIENT_RESET_PASSWORD_PAGE_LINK } from 'commons/constants/links';
import PhoneNumberField from 'commons/components/fields/PhoneNumberField/PhoneNumberField';
import { storeName } from 'store/models/user.model';
import { storeName as layoutModelName } from 'store/models/layout.model';
import GoogleRecaptcha from 'commons/components/GoogleRecaptcha';
import LanguageSwitcher from 'commons/components/LanguageSwitcher';
import { RootState } from 'store/store';
import { requestPhoneResetOtp, verifyPhoneResetOtp } from 'services/external/resetPassword';
import './styles.scss';

type ResetMode = 'EMAIL' | 'PHONE';

const ClientForgotPasswordPage: FC = () => {
  const language = (() => { try { return localStorage.getItem('i18nextLng') || 'vi-VN'; } catch (_) { return 'vi-VN'; } })();
  i18next.changeLanguage(language);

  const user = useSelector((state: RootState) => state[storeName].user);
  const loading = useSelector((state: RootState) => state[storeName].loading);
  const layoutDispatch = useDispatch()[layoutModelName];
  const userDispatch = useDispatch()[storeName];

  const [mode, setMode] = useState<ResetMode>('EMAIL');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isCaptchaValid, setCaptchaValid] = useState(false);

  layoutDispatch.setLoading(false);

  if (user) {
    return <Redirect to="/" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isCaptchaValid) {
      setError(i18next.t('client-forgot-password.message-captcha-error'));
      return;
    }

    setCaptchaValid(false);
    if (mode === 'PHONE') {
      const res = await requestPhoneResetOtp({ phoneNumber });
      if (res && res.data) {
        setOtpStep(true);
        setSuccess(true);
      }
      return;
    }

    const res = await userDispatch.resetPassword({ email });
    if (res && res.data) {
      setSuccess(true);
      setEmail('');
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const res = await verifyPhoneResetOtp({ phoneNumber, otpCode });
    if (res && res.data) {
      window.location.href = `${CLIENT_RESET_PASSWORD_PAGE_LINK}?token=${res.data}`;
    }
  };

  return (
    <div className="cfp-page">
      <div className="cfp-card">
          <LanguageSwitcher />

          <h1 className="cfp-heading">{i18next.t('client-forgot-password.title')}</h1>
          <p className="cfp-subtitle">{i18next.t('client-forgot-password.subtitle')}</p>

          {success && (
            <p className="cfp-success">
              {otpStep
                ? i18next.t('client-forgot-password.message-otp-success')
                : i18next.t('client-forgot-password.message-success')}
            </p>
          )}

          <div className="cfp-mode" role="tablist">
            <button className={`cfp-mode-btn${mode === 'EMAIL' ? ' cfp-mode-btn--active' : ''}`} type="button" onClick={() => { setMode('EMAIL'); setOtpStep(false); setError(null); }}>
              {i18next.t('client-forgot-password.option-email')}
            </button>
            <button className={`cfp-mode-btn${mode === 'PHONE' ? ' cfp-mode-btn--active' : ''}`} type="button" onClick={() => { setMode('PHONE'); setOtpStep(false); setError(null); }}>
              {i18next.t('client-forgot-password.option-phone')}
            </button>
          </div>

          {otpStep ? (
            <form onSubmit={handleVerifyOtp} noValidate>
              <div className="cfp-field">
                <label className="cfp-label" htmlFor="cfp-otp">{i18next.t('client-forgot-password.field-otp')}</label>
                <input
                  id="cfp-otp"
                  className="cfp-input cfp-input--otp"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
                  required
                />
              </div>

              {error && <span className="cfp-error">{error}</span>}

              <button className="cfp-btn-primary" type="submit" disabled={!otpCode}>
                {i18next.t('client-forgot-password.btn-verify')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {mode === 'EMAIL' ? (
                <div className="cfp-field">
                  <label className="cfp-label" htmlFor="cfp-email">{i18next.t('client-forgot-password.field-email')}</label>
                  <input
                    id="cfp-email"
                    className="cfp-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    required
                  />
                </div>
              ) : (
                <div className="cfp-field">
                  <span className="cfp-label">{i18next.t('client-forgot-password.field-phone')}</span>
                  <PhoneNumberField
                    placeholder="+84 xxx xxx xxx"
                    value={phoneNumber}
                    onChange={(value) => { setPhoneNumber(value || ''); setError(null); }}
                  />
                </div>
              )}

              <div className="cfp-captcha">
                <GoogleRecaptcha
                  onChange={(value) => setCaptchaValid(!!value)}
                  isReset={isCaptchaValid}
                />
              </div>

              {error && <span className="cfp-error">{error}</span>}

              <button
                className="cfp-btn-primary"
                type="submit"
                disabled={!isCaptchaValid || loading}
              >
                {loading
                  ? i18next.t('client-forgot-password.btn-sending')
                  : i18next.t('client-forgot-password.btn-send')}
              </button>
            </form>
          )}

          <p className="cfp-footer">
            <a href={CLIENT_LOGIN_LINK}>
              {i18next.t('client-forgot-password.footer-link')}
            </a>
          </p>
      </div>
    </div>
  );
};

export default ClientForgotPasswordPage;
